import Vue from 'vue'
import Router from 'vue-router'
import store from '../store'
import { pluck } from '../utilities'
import { routeAllowed, role, allRoles, allAdmins } from './permissions'

Vue.use(Router)

const tableProps = (params) => (route) => {
    var props = route.query
    props.page = parseInt(route.query.page || 1)
    props.per_page = parseInt(route.query.per_page || 25)
    props.sort_dir = route.query.sort_dir
    props.sort_by = route.query.sort_by
    return Object.assign(props, pluck(route.params, params))
}

const reportProps = (route) => {
    var props = route.query
    props.page = parseInt(route.query.page || 1)
    if (route.query.per_page) {
        props.per_page = parseInt(route.query.per_page)
    }
    props.sort_dir = route.query.sort_dir
    props.sort_by = route.query.sort_by
    props.classes = route.query.classes ? parseInt(route.query.classes) : null
    props.start_date = route.query.start_date
    props.end_date = route.query.end_date
    return props
}

const router = new Router({
    mode: 'history',
    routes: [
        {
            path: '/',
            name: 'home',
            redirect: '/Dashboard'
        },
        {
            path: '/Login',
            name: 'login',
            component: () => import('../components/pages/Auth/LoginView.vue'),
            meta: { guest: true }
        },
        {
            path: '/Logout',
            name: 'logout',
            beforeEnter: (to, from, next) => {
                store.dispatch('logout').then(() => {
                    next({ name: 'login' })
                })
            }
        },
        {
            path: '/Invite/:uuid',
            name: 'invite',
            component: () => import('../components/pages/Auth/InviteView.vue'),
            props: true,
            meta: { guest: true }
        },
        {
            path: '/ForgotPassword',
            name: 'password.forgot',
            component: () => import('../components/pages/Auth/ForgotPasswordView.vue'),
            meta: { guest: true }
        },
        {
            path: '/ResetPassword/:uuid',
            name: 'password.reset',
            component: () => import('../components/pages/Auth/ResetPasswordView.vue'),
            props: true,
            meta: { guest: true }
        },
        {
            path: '/EventSignup/:uuid',
            name: 'events.signup',
            component: () => import('../components/pages/Auth/EventSignupView.vue'),
            props: true,
            meta: { guest: true }
        },


        /// Dashboard
        {
            path: '/Dashboard',
            name: 'dashboard',
            component: () => import('../components/pages/Dashboard/DashboardView.vue'),
            meta: { roles: allRoles }
        },


        /// Students
        {
            path: '/Students',
            name: 'students',
            component: () => import('../components/pages/Student/StudentListView.vue'),
            props: tableProps(),
            meta: { roles: allRoles }
        },
        {
            path: '/Students/:uuid',
            component: () => import('../components/pages/Student/StudentDetailView.vue'),
            props: true,
            meta: { roles: allRoles },
            children: [
                {
                    path: '',
                    name: 'students.detail',
                    redirect: 'Courses'
                },
                {
                    path: 'Courses',
                    name: 'students.detail.courses',
                    component: () => import('../components/pages/Student/StudentCoursesView.vue'),
                    props: true,
                    meta: { roles: allRoles }
                },
                {
                    path: 'Events',
                    name: 'students.detail.events',
                    component: () => import('../components/pages/Student/StudentEventsView.vue'),
                    props: tableProps([ 'uuid' ]),
                    meta: { roles: allRoles }
                },
                {
                    path: 'Information',
                    name: 'students.detail.information',
                    component: () => import('../components/pages/Student/StudentInformationView.vue'),
                    props: true,
                    meta: { roles: allRoles }
                }
            ]
        },
        {
            path: '/Students/:studentUuid/Attendance/:courseUuid',
            name: 'attendance',
            component: () => import('../components/pages/Student/AttendanceView.vue'),
            props: true,
            meta: { roles: allRoles }
        },
        {
            path: '/Students/:studentUuid/Events/:attendableUuid/Attendance',
            name: 'students.detail.events.attendance',
            component: () => import('../components/pages/Student/AttendanceView.vue'),
            props: true,
            meta: { roles: allRoles }
        },
        {
            path: '/Students/:studentUuid/Courses/:attendableUuid/Attendance',
            name: 'students.detail.courses.attendance',
            component: () => import('../components/pages/Student/AttendanceView.vue'),
            props: true,
            meta: { roles: allRoles }
        },


        /// Courses
        {
            path: '/Courses',
            name: 'courses',
            component: () => import('../components/pages/Course/CourseListView.vue'),
            props: tableProps(),
            meta: { roles: allRoles },
        },
        {
            path: '/Courses/:uuid',
            component: () => import('../components/pages/Course/CourseDetailView.vue'),
            props: true,
            meta: { roles: allRoles },
            children: [
                {
                    path: '',
                    name: 'courses.detail',
                    redirect: 'Dashboard'
                },
                {
                    path: 'Dashboard',
                    name: 'courses.detail.dashboard',
                    component: () => import('../components/pages/Course/CourseDashboardView.vue'),
                    props: true,
                    meta: { roles: allRoles },
                },
                {
                    path: 'Students',
                    name: 'courses.detail.students',
                    component: () => import('../components/pages/Course/CourseStudentsView.vue'),
                    props: tableProps([ 'uuid' ]),
                    meta: { roles: allRoles },
                },
                {
                    path: 'Cancellations',
                    name: 'courses.detail.cancellations',
                    component: () => import('../components/pages/Course/CourseCancellationView.vue'),
                    props: true,
                    meta: { roles: allRoles },
                },
                {
                    path: 'Users',
                    name: 'courses.detail.users',
                    component: () => import('../components/pages/Course/CourseUsersView.vue'),
                    props: true,
                    meta: { roles: allRoles }
                }
            ]
        },


        /// Events
        {
            path: '/Events',
            name: 'events',
            component: () => import('../components/pages/Event/EventListView.vue'),
            props: tableProps(),
            meta: { roles: allRoles }
        },
        {
            path: '/Events/:uuid',
            component: () => import('../components/pages/Event/EventDetailView.vue'),
            props: true,
            meta: { roles: allRoles },
            children: [
                {
                    path: '',
                    name: 'events.detail',
                    redirect: 'Dashboard'
                },
                {
                    path: 'Dashboard',
                    name: 'events.detail.dashboard',
                    component: () => import('../components/pages/Event/EventDashboardView.vue'),
                    props: true,
                    meta: { roles: allRoles }
                },
                {
                    path: 'Students',
                    name: 'events.detail.students',
                    component: () => import('../components/pages/Event/EventStudentsView.vue'),
                    props: tableProps([ 'uuid' ]),
                    meta: { roles: allRoles }
                },
                {
                    path: 'Waitlist',
                    name: 'events.detail.waitlist',
                    component: () => import('../components/pages/Event/EventWaitlistView.vue'),
                    props: true,
                    meta: { roles: allRoles }
                },
                {
                    path: 'Users',
                    name: 'events.detail.users',
                    component: () => import('../components/pages/Event/EventUsersView.vue'),
                    props: true,
                    meta: { roles: allRoles }
                }
            ]
        },


        /// Teams
        {
            path: '/Teams',
            name: 'teams',
            component: () => import('../components/pages/Team/TeamListView.vue'),
            props: tableProps(),
            meta: { roles: allRoles }
        },
        {
            path: '/Teams/:uuid',
            component: () => import('../components/pages/Team/TeamDetailView.vue'),
            props: true,
            meta: { roles: allRoles },
            children: [
                {
                    path: '',
                    name: 'teams.detail',
                    redirect: 'Students'
                },
                {
                    path: 'Students',
                    name: 'teams.detail.students',
                    component: () => import('../components/pages/Team/TeamStudentsView.vue'),
                    props: tableProps([ 'uuid' ]),
                    meta: { roles: allRoles }
                },
                {
                    path: 'Travel',
                    name: 'teams.detail.travel',
                    component: () => import('../components/pages/Team/TeamTravelView.vue'),
                    props: true,
                    meta: { roles: [ ...allAdmins, role.academic_advisor, role.coach ] }
                },
                {
                    path: 'Users',
                    name: 'teams.detail.users',
                    component: () => import('../components/pages/Team/TeamUsersView.vue'),
                    props: true,
                    meta: { roles: allRoles }
                }
            ]
        },
        

        /// Reports
        {
            path: '/Reports',
            name: 'reports',
            component: () => import('../components/pages/Report/ReportsView.vue'),
            meta: { roles: allRoles }
        },
        {
            path: '/Reports/Status',
            name: 'reports.status',
            component: () => import('../components/pages/Report/StatusReportView.vue'),
            props: reportProps,
            meta: { roles: allRoles }
        },
        {
            path: '/Reports/Cancellation',
            name: 'reports.cancellation',
            component: () => import('../components/pages/Report/CancellationReportView.vue'),
            props: reportProps,
            meta: { roles: allRoles }
        },
        {
            path: '/Reports/Canvas',
            name: 'reports.canvas',
            component: () => import('../components/pages/Report/CanvasReportView.vue'),
            props: reportProps,
            meta: { roles: allRoles }
        },
        {
            path: '/Reports/Class',
            name: 'reports.class',
            component: () => import('../components/pages/Report/ClassReportView.vue'),
            props: reportProps,
            meta: { roles: allRoles }
        },
        {
            path: '/Reports/Student',
            name: 'reports.student',
            component: () => import('../components/pages/Report/StudentReportView.vue'),
            props: reportProps,
            meta: { roles: allRoles }
        },
        {
            path: '/Reports/Team',
            name: 'reports.team',
            component: () => import('../components/pages/Report/TeamReportView.vue'),
            props: reportProps,
            meta: { roles: allRoles },
        },
        {
            path: '/Reports/Event',
            name: 'reports.event',
            component: () => import('../components/pages/Report/EventReportView.vue'),
            props: reportProps,
            meta: { roles: allRoles }
        },
        {
            path: '/Reports/LastAttendance',
            name: 'reports.lastattendance',
            component: () => import('../components/pages/Report/LastAttendanceReportView.vue'),
            props: reportProps,
            meta: { roles: allRoles },
        },
        {
            path: '/Reports/Beacon',
            name: 'reports.beacon',
            component: () => import('../components/pages/Report/BeaconReportView.vue'),
            props: reportProps,
            meta: { roles: [...allAdmins] }
        },
        {
            path: '/Reports/Setup',
            name: 'reports.setup',
            component: () => import('../components/pages/Report/SetupReportView.vue'),
            props: reportProps,
            meta: { roles: allRoles },
        },
        {
            path: '/Reports/Token',
            name: 'reports.token',
            component: () => import('../components/pages/Report/TokenReportView.vue'),
            props: reportProps,
            meta: { roles: allRoles },
        },
        {
            path: '/Reports/Presence',
            name: 'reports.presence',
            component: () => import('../components/pages/Report/PresenceReportView.vue'),
            props: reportProps,
            meta: { roles: allAdmins }
        },


        /// Users
        {
            path: '/Users',
            name: 'users',
            component: () => import('../components/pages/User/UserListView.vue'),
            props: tableProps(),
            meta: { roles: [ ...allAdmins, role.academic_advisor, role.coach, role.professor ] }
        },
        {
            path: '/Users/:id',
            component: () => import('../components/pages/User/UserDetailView.vue'),
            props: (route) => ({ id: parseInt(route.params.id) }),
            meta: { roles: allRoles },
            children: [
                {
                    path: '',
                    name: 'users.detail',
                    redirect: 'Information',
                    meta: { roles: allRoles }
                },
                {
                    path: 'Information',
                    name: 'users.detail.information',
                    component: () => import('../components/pages/User/UserInformationView.vue'),
                    props: true,
                    meta: { roles: allRoles }
                },
                {
                    path: 'Access',
                    component: () => import('../components/pages/User/UserAccessView.vue'),
                    meta: { roles: allRoles },
                    props: true,
                    children: [
                        {
                            path: '',
                            name: 'users.detail.access',
                            redirect: 'Courses',
                            meta: { roles: allRoles }
                        },
                        {
                            path: 'Courses',
                            name: 'users.detail.access.courses',
                            component: () => import('../components/pages/User/UserAccessCoursesView.vue'),
                            props: true,
                            meta: { roles: allRoles }
                        },
                        {
                            path: 'Events',
                            name: 'users.detail.access.events',
                            component: () => import('../components/pages/User/UserAccessEventsView.vue'),
                            props: true,
                            meta: { roles: allRoles }
                        },
                        {
                            path: 'Students',
                            name: 'users.detail.access.students',
                            component: () => import('../components/pages/User/UserAccessStudentsView.vue'),
                            props: true,
                            meta: { roles: allRoles }
                        },
                        {
                            path: 'Teams',
                            name: 'users.detail.access.teams',
                            component: () => import('../components/pages/User/UserAccessTeamsView.vue'),
                            props: true,
                            meta: { roles: allRoles }
                        },
                    ]
                },
                {
                    path: 'Security',
                    name: 'users.detail.security',
                    component: () => import('../components/pages/User/UserSecurityView.vue'),
                    props: true,
                    meta: { roles: allRoles }
                },
                {
                    path: 'Notifications',
                    name: 'users.detail.notifications',
                    component: () => import('../components/pages/User/UserNotificationsView.vue'),
                    props: true,
                    meta: { roles: allRoles }
                }
            ]
        },


        /// Settings
        {
            path: '/Settings',
            component: () => import('../components/pages/Settings/SettingsView.vue'),
            meta: { roles: [ ...allAdmins ] },
            children: [
                {
                    path: '',
                    redirect: 'School'
                },
                {
                    path: 'School',
                    name: 'settings.school',
                    component: () => import('../components/pages/Settings/SchoolView.vue'),
                    props: true,
                    meta: { roles: [ ...allAdmins ] }
                },
                {
                    path: 'Locations',
                    name: 'settings.locations',
                    component: () => import('../components/pages/Location/LocationListView.vue'),
                    props: tableProps(),
                    meta: { roles: [ ...allAdmins ] }
                },
                {
                    path: 'Imports',
                    name: 'settings.imports',
                    component: () => import('../components/pages/Settings/ImportsView.vue'),
                    meta: { roles: [ ...allAdmins ] }
                },
                {
                    path: 'Technical',
                    name: 'settings.technical',
                    component: () => import('../components/pages/Settings/TechnicalView.vue'),
                    meta: { roles: [ ...allAdmins ] }
                }
            ]
        },
        {
            path: '/Locations/:id',
            name: 'locations.detail',
            component: () => import('../components/pages/Location/LocationDetailView.vue'),
            props: true,
            meta: { roles: [ ...allAdmins ] }
        },
        {
            path: '/Imports/:uuid',
            name: 'imports.detail',
            component: () => import('../components/pages/Settings/ImportView.vue'),
            props: true,
            meta: { roles: [ ...allAdmins ] }
        }
    ]
});

router.beforeEach((to, from, next) => {

    // Make sure you always let people login
    if (to.meta.guest && !store.state.me.isLoggedIn) {
        next();
        return;
    }

    // Ensure we have a user object before we start routing
    store
        .dispatch('checkUserLoad')
        .then( () => {

        // Check to make sure the user is allowed to view page
            if (!routeAllowed(to)) {
                next({ name: 'dashboard' })
                return
            }

            // Save previous page on every route change
            if (from.name != "login") {
                localStorage.setItem('prevPage', from.path);
            }

            // If you are trying to go to the login screen, send you to home
            if (to.meta.guest && store.state.me.isLoggedIn) {
                next({ name: 'dashboard' })
                return
            }

            // If everything is still golden, lets keep going
            next();

        }, () => {
            next({
                name: 'login',
                query: { redirect: to.fullPath }
            })
        })
})

router.afterEach((to) => {
    const path = window.location.origin + to.fullPath
    window.Beacon('event', {
        type: 'page-viewed',
        url: path,
        title: to.name,
    })
})

export default router
